import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import Navbar from "../Home/Navber";
import Footer from "../Home/Footer";
import { getProperties, OrderProperty, VerifyPayment } from "../../Services/Operations/ProductAPI";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

/* ── Design tokens (same as your existing theme) ── */
const GOLD = "#e78610";
const TAN = "#bba58b";
const WHITE = "#ffffff";
const DARK = "#1a1208";

/* ── Dummy service data ── */
const SERVICEDUMMY = {
  id: 4,
  package_name: "Office Interior Setup",
  category: "office",
  description: "Modern office design with a focus on productivity, aesthetics, and employee well-being. Our expert team crafts workspaces that inspire creativity and foster collaboration, blending contemporary design principles with functional layouts tailored to your brand identity.",
  base_price: "300000.00",
  price_type: "per_sqft",
  min_area: 500,
  max_area: 2000,
  duration_days: 30,
  material_details: "Glass partitions, modular workstations, ergonomic furniture, acoustic ceiling tiles, LED ambient lighting systems",
  work_scope: "Full interior design, custom furniture procurement, lighting design, electrical & data cabling, false ceiling, flooring",
  status: "active",
  created_at: "2026-04-09 08:01:42",
  updated_at: "2026-04-09 08:01:42",
};

const REVIEWS = [
  { id: 1, name: "Arjun Mehta", role: "CEO, TechNova", rating: 5, date: "March 2026", text: "Absolutely transformed our workspace. The attention to detail was remarkable — every corner felt intentional. Our team productivity has visibly improved since the renovation.", avatar: "AM" },
  { id: 2, name: "Priya Sharma", role: "Director, Bloom Studios", rating: 5, date: "February 2026", text: "Premium quality from start to finish. The glass partitions and lighting setup exceeded expectations. Visitors always compliment how refined the office feels.", avatar: "PS" },
  { id: 3, name: "Rahul Bose", role: "Founder, Kova Digital", rating: 4, date: "January 2026", text: "Delivered exactly on schedule, which is rare in the interior industry. The acoustic panels made a huge difference in meeting room privacy. Highly recommend.", avatar: "RB" },
  { id: 4, name: "Sneha Roy", role: "HR Head, FinEdge", rating: 5, date: "December 2025", text: "The modular workstation design was genius — we've rearranged the layout twice already and it works perfectly. Our employees love coming to the office now.", avatar: "SR" },
];

const PROCESS = [
  { step: "01", title: "Consultation", desc: "We begin with an in-depth discussion about your vision, brand identity, and functional requirements." },
  { step: "02", title: "Design & Planning", desc: "Our designers craft detailed 3D layouts and material boards for your approval before any work begins." },
  { step: "03", title: "Procurement", desc: "Premium materials and custom furniture are sourced from our curated network of vendors." },
  { step: "04", title: "Execution", desc: "Skilled craftsmen handle installation with precision, adhering strictly to timelines and quality benchmarks." },
  { step: "05", title: "Handover", desc: "A thorough walkthrough and snag-fixing session ensures every detail meets our luxury standard." },
];

const CATEGORY_CONFIG = {
  office: {
    img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&q=80",
    img2: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80",
    img3: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&q=80",
    icon: "🏛️",
    label: "Office Interiors",
  },
};

const cat = CATEGORY_CONFIG.office;

const formatPrice = (price, type) => {
  const num = parseFloat(price).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
  return type === "per_sqft" ? `${num} / sq.ft` : num;
};

/* ── Star rating ── */
const Stars = ({ rating, size = 13 }) => (
  <span style={{ display: "flex", gap: 2 }}>
    {[1,2,3,4,5].map(s => (
      <svg key={s} viewBox="0 0 24 24" style={{ width: size, height: size, fill: s <= rating ? GOLD : "rgba(187,165,139,0.2)", stroke: "none" }}>
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ))}
  </span>
);

/* ── Inline SVG icons ── */
const Ico = ({ d, size = 16, color = TAN, sw = 1.7 }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, stroke: color, fill: "none", strokeWidth: sw, strokeLinecap: "round", strokeLinejoin: "round", flexShrink: 0 }}>
    {Array.isArray(d) ? d.map((p,i) => <path key={i} d={p}/>) : <path d={d}/>}
  </svg>
);

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Josefin+Sans:wght@300;400;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root { --gold: #e78610; --tan: #bba58b; --dark: #1a1208; }

  .sd-page {
    min-height: 100vh;
    background:
      radial-gradient(ellipse at 70% 10%, rgba(240,213,180,0.11) 0%, transparent 55%),
      radial-gradient(ellipse at 10% 85%, rgba(226,194,155,0.13) 0%, transparent 50%),
      linear-gradient(145deg, #1e1a14 0%, #2c2620 40%, #241e17 70%, #1a1510 100%);
    font-family: 'Josefin Sans', sans-serif;
    color: ${WHITE};
    position: relative; overflow-x: hidden;
  }

  /* BG grid */
  .sd-grid-bg { position:fixed; inset:0; opacity:0.04; pointer-events:none; z-index:0; }

  /* Hero */
  .sd-hero {
    position: relative; height: 520px; overflow: hidden;
  }
  .sd-hero-img {
    width:100%; height:100%; object-fit:cover; display:block;
    filter: brightness(0.45);
  }
  .sd-hero-overlay {
    position:absolute; inset:0;
    background: linear-gradient(180deg, rgba(26,18,8,0.2) 0%, rgba(26,18,8,0.85) 75%, rgba(26,18,8,1) 100%);
  }
  .sd-hero-content {
    position:absolute; bottom:0; left:0; right:0;
    padding: 0 48px 48px;
    max-width:1100px; margin:0 auto;
  }
  .sd-hero-badge {
    display:inline-flex; align-items:center; gap:7px;
    background:rgba(231,134,16,0.15); border:1px solid rgba(231,134,16,0.3);
    border-radius:20px; padding:5px 14px; margin-bottom:16px;
    font-size:9px; letter-spacing:0.18em; text-transform:uppercase;
    color:${GOLD}; font-weight:600;
  }
  .sd-hero-title {
    font-family:'Cormorant Garamond', serif;
    font-size:clamp(2.2rem, 5vw, 3.4rem);
    font-weight:600; line-height:1.1; letter-spacing:0.02em;
    color:${WHITE}; margin-bottom:14px;
  }
  .sd-hero-meta {
    display:flex; gap:24px; flex-wrap:wrap; align-items:center;
  }
  .sd-hero-meta-item {
    display:flex; align-items:center; gap:6px;
    font-size:11px; color:rgba(187,165,139,0.75);
    letter-spacing:0.08em;
  }
  .sd-gold-line {
    width:50px; height:2px; border-radius:2px;
    background:linear-gradient(90deg, ${GOLD}, #f5c56a);
    margin-bottom:18px;
  }

  /* Layout */
  .sd-body {
    position:relative; z-index:1;
    max-width:1100px; margin:0 auto;
    padding:48px 24px 80px;
    display:grid;
    grid-template-columns:1fr 340px;
    gap:32px;
    align-items:start;
  }

  /* Cards */
  .sd-card {
    background:linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(187,165,139,0.03) 100%);
    border:1px solid rgba(187,165,139,0.14);
    border-radius:20px; overflow:hidden;
    backdrop-filter:blur(12px);
  }
  .sd-card-head {
    padding:22px 28px 16px;
    border-bottom:1px solid rgba(187,165,139,0.1);
    display:flex; align-items:center; gap:10px;
  }
  .sd-card-head-icon {
    width:34px; height:34px; border-radius:10px;
    background:linear-gradient(135deg, rgba(231,134,16,0.25), rgba(199,113,10,0.15));
    border:1px solid rgba(231,134,16,0.3);
    display:flex; align-items:center; justify-content:center;
    font-size:15px;
  }
  .sd-card-title {
    font-family:'Josefin Sans', sans-serif;
    font-size:9px; font-weight:600; color:${GOLD};
    letter-spacing:0.2em; text-transform:uppercase;
  }
  .sd-card-body { padding:24px 28px; }

  /* Description */
  .sd-desc {
    font-size:13.5px; font-weight:300; color:rgba(187,165,139,0.75);
    line-height:1.85; letter-spacing:0.03em;
  }

  /* Image gallery */
  .sd-gallery {
    display:grid; grid-template-columns:1fr 1fr; gap:12px;
    margin-top:24px;
  }
  .sd-gallery-img {
    width:100%; height:150px; object-fit:cover;
    border-radius:12px; display:block;
    border:1px solid rgba(187,165,139,0.1);
    transition:transform 0.4s ease, filter 0.3s;
    filter:brightness(0.8);
  }
  .sd-gallery-img:hover { transform:scale(1.03); filter:brightness(1); }

  /* Stats strip */
  .sd-stats {
    display:grid; grid-template-columns:repeat(4,1fr);
    gap:1px; background:rgba(187,165,139,0.1);
    border-radius:16px; overflow:hidden;
    margin-bottom:24px;
  }
  .sd-stat-item {
    background:rgba(20,14,6,0.6);
    padding:18px 14px; text-align:center;
    transition:background 0.2s;
  }
  .sd-stat-item:hover { background:rgba(231,134,16,0.07); }
  .sd-stat-val {
    font-family:'Cormorant Garamond', serif;
    font-size:22px; font-weight:600; color:${WHITE};
    line-height:1; margin-bottom:5px;
  }
  .sd-stat-label {
    font-size:8px; color:rgba(187,165,139,0.45);
    letter-spacing:0.14em; text-transform:uppercase;
  }

  /* Process */
  .sd-process-item {
    display:flex; gap:18px; padding:16px 0;
    border-bottom:1px solid rgba(187,165,139,0.08);
    transition:background 0.2s;
  }
  .sd-process-item:last-child { border-bottom:none; }
  .sd-process-num {
    font-family:'Cormorant Garamond', serif;
    font-size:28px; font-weight:300; color:rgba(231,134,16,0.35);
    line-height:1; flex-shrink:0; width:36px; text-align:right;
    transition:color 0.3s;
  }
  .sd-process-item:hover .sd-process-num { color:rgba(231,134,16,0.7); }
  .sd-process-step-title {
    font-family:'Josefin Sans', sans-serif;
    font-size:11px; font-weight:600; color:${WHITE};
    letter-spacing:0.1em; margin-bottom:5px;
  }
  .sd-process-desc {
    font-size:11.5px; font-weight:300;
    color:rgba(187,165,139,0.6); line-height:1.65;
    letter-spacing:0.02em;
  }

  /* Reviews */
  .sd-review-card {
    background:rgba(255,255,255,0.03);
    border:1px solid rgba(187,165,139,0.1);
    border-radius:14px; padding:20px 22px;
    margin-bottom:14px; transition:border-color 0.3s, transform 0.2s;
  }
  .sd-review-card:hover {
    border-color:rgba(231,134,16,0.25);
    transform:translateY(-2px);
  }
  .sd-review-header { display:flex; align-items:center; gap:12px; margin-bottom:12px; }
  .sd-review-avatar {
    width:40px; height:40px; border-radius:50%;
    background:linear-gradient(135deg, rgba(231,134,16,0.3), rgba(187,165,139,0.2));
    border:1px solid rgba(231,134,16,0.3);
    display:flex; align-items:center; justify-content:center;
    font-family:'Cormorant Garamond', serif;
    font-size:14px; font-weight:600; color:${GOLD};
    flex-shrink:0;
  }
  .sd-review-name {
    font-size:12px; font-weight:600; color:${WHITE};
    margin-bottom:2px; letter-spacing:0.05em;
  }
  .sd-review-role {
    font-size:9px; color:rgba(187,165,139,0.5);
    letter-spacing:0.1em; text-transform:uppercase;
  }
  .sd-review-date {
    font-size:9px; color:rgba(187,165,139,0.35);
    letter-spacing:0.08em; margin-left:auto; align-self:flex-start;
  }
  .sd-review-text {
    font-size:12px; font-weight:300; color:rgba(187,165,139,0.65);
    line-height:1.75; letter-spacing:0.02em;
  }

  /* Rating summary */
  .sd-rating-summary {
    display:flex; align-items:center; gap:20px;
    padding:18px 24px;
    background:rgba(231,134,16,0.06);
    border:1px solid rgba(231,134,16,0.15);
    border-radius:14px; margin-bottom:22px;
  }
  .sd-rating-big {
    font-family:'Cormorant Garamond', serif;
    font-size:48px; font-weight:600; color:${WHITE};
    line-height:1;
  }
  .sd-rating-right { flex:1; }
  .sd-rating-count {
    font-size:9px; color:rgba(187,165,139,0.45);
    letter-spacing:0.12em; text-transform:uppercase; margin-top:5px;
  }

  /* Sidebar */
  .sd-sidebar { position:sticky; top:24px; }

  .sd-price-card {
    background:linear-gradient(145deg, rgba(26,18,8,0.95), rgba(20,14,5,0.95));
    border:1px solid rgba(231,134,16,0.28);
    border-radius:20px; overflow:hidden;
    box-shadow:0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(231,134,16,0.08);
    margin-bottom:20px; position:relative;
  }
  .sd-price-shimmer {
    position:absolute; top:0; left:10%; width:80%; height:2px;
    background:linear-gradient(90deg, transparent, ${GOLD}, #f5c56a, ${GOLD}, transparent);
    background-size:200% 100%;
    animation:shimmer-h 3s linear infinite;
  }
  @keyframes shimmer-h {
    0%   { background-position:-200% 0; }
    100% { background-position: 200% 0; }
  }

  .sd-price-head { padding:24px 24px 18px; text-align:center; }
  .sd-price-label {
    font-size:8px; letter-spacing:0.2em; text-transform:uppercase;
    color:rgba(187,165,139,0.45); margin-bottom:10px;
  }
  .sd-price-amount {
    font-family:'Cormorant Garamond', serif;
    font-size:36px; font-weight:600; color:${GOLD};
    line-height:1; letter-spacing:0.01em;
  }
  .sd-price-type {
    font-size:9px; color:rgba(187,165,139,0.4);
    letter-spacing:0.12em; text-transform:uppercase; margin-top:5px;
  }

  .sd-price-rows {
    border-top:1px solid rgba(187,165,139,0.08);
    padding:16px 24px;
  }
  .sd-price-row {
    display:flex; justify-content:space-between; align-items:center;
    padding:9px 0;
    border-bottom:1px solid rgba(187,165,139,0.06);
    font-size:11px;
  }
  .sd-price-row:last-child { border-bottom:none; }
  .sd-price-row-label { color:rgba(187,165,139,0.45); letter-spacing:0.07em; }
  .sd-price-row-val { color:${WHITE}; font-weight:600; }

  .sd-book-btn {
    display:block; width:calc(100% - 48px); margin:0 24px 24px;
    padding:14px; border-radius:14px; border:none; cursor:pointer;
    background:linear-gradient(135deg, #f0a84a 0%, ${GOLD} 50%, #c97d22 100%);
    color:${DARK}; font-family:'Josefin Sans', sans-serif;
    font-size:11px; font-weight:700; letter-spacing:0.2em; text-transform:uppercase;
    box-shadow:0 8px 24px rgba(231,134,16,0.38), inset 0 1px 0 rgba(255,255,255,0.25);
    transition:transform 0.15s, box-shadow 0.2s;
    display:flex; align-items:center; justify-content:center; gap:8px;
  }
  .sd-book-btn:hover {
    transform:translateY(-2px);
    box-shadow:0 12px 32px rgba(231,134,16,0.48);
  }
  .sd-book-btn:active { transform:scale(0.98); }

  .sd-consult-btn {
    display:flex; align-items:center; justify-content:center; gap:7px;
    width:calc(100% - 48px); margin:0 24px 24px;
    padding:12px; border-radius:14px; border:1px solid rgba(187,165,139,0.22);
    background:rgba(187,165,139,0.06); cursor:pointer;
    color:rgba(255,255,255,0.6); font-family:'Josefin Sans', sans-serif;
    font-size:10px; font-weight:600; letter-spacing:0.14em; text-transform:uppercase;
    transition:all 0.25s;
  }
  .sd-consult-btn:hover {
    background:rgba(187,165,139,0.12); border-color:rgba(187,165,139,0.35);
    color:${WHITE};
  }

  /* Materials chips */
  .sd-chips { display:flex; flex-wrap:wrap; gap:8px; }
  .sd-chip {
    padding:5px 13px; border-radius:20px; font-size:9.5px; font-weight:600;
    letter-spacing:0.08em; text-transform:uppercase;
    background:rgba(187,165,139,0.08); border:1px solid rgba(187,165,139,0.15);
    color:rgba(187,165,139,0.7); transition:all 0.2s;
  }
  .sd-chip:hover {
    background:rgba(231,134,16,0.12); border-color:rgba(231,134,16,0.3);
    color:${GOLD};
  }

  /* Booking modal */
  .sd-modal-row {
    display:flex; justify-content:space-between;
    padding:11px 0; border-bottom:1px solid rgba(187,165,139,0.08);
  }
  .sd-modal-row:last-child { border-bottom:none; }

  /* Responsive */
  @media (max-width:900px) {
    .sd-body { grid-template-columns:1fr; }
    .sd-sidebar { position:static; }
    .sd-stats { grid-template-columns:repeat(2,1fr); }
    .sd-hero-content { padding:0 24px 36px; }
    .sd-hero { height:400px; }
  }
  @media (max-width:560px) {
    .sd-stats { grid-template-columns:1fr 1fr; }
    .sd-gallery { grid-template-columns:1fr; }
    .sd-hero { height:340px; }
    .sd-body { padding:32px 16px 60px; }
  }
`;

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function ServiceDetail() {
    const id=useParams()
       const { isAuthenticated ,token} = useSelector((state) => state.auth);
    const[SERVICE,SETSERVICE]=useState(SERVICEDUMMY)
  const [showModal, setShowModal] = useState(false);
  const materials = SERVICE.material_details.split(",").map(s => s.trim());
  const scope = SERVICE.work_scope.split(",").map(s => s.trim());
  const navigate=useNavigate()

   
    const handleGetService=async()=>{
      try {
        const res= await getProperties()
        console.log(res);
        if(res.status===200){
         // setServices(res.data.data)
         const item=res.data.data.find(i=>i.id===Number(id.id))
         SETSERVICE(item)
        }
        
      } catch (error) {
        console.log(error);
        
      }
    }
  
    useEffect(() => {
      handleGetService()
    }, [])

          const handleOrder=async()=>{
            if(!token){
                toast.error("Login to make booking")
                navigate("/login")
            }
            try { let data={property_id:Number(id.id)}
                const res=await OrderProperty(data,token)
                console.log(res);
                if(res.status===200){
                    toast.success("Order Booked")
                    handlePayment(res.data)
                }
              //  
                
            } catch (error) {
                console.log(error);
                
            }
        }
    
              const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };
    
    const verifyPayment=async(data)=>{
    try {
      if(!token){
         toast.warning("Unauthorized login first")
            navigate("/login")
      }
      console.log(data);
      const res= await VerifyPayment(data,token)
      console.log(res);
      if(res.status===200){
        toast.success(
    "Payment successful & property booked")
    setShowModal(false)
    
      }
      
      
      
    } catch (error) {
      console.log(error);
      toast.error("Payment failed")
    }
    }
    
    
      const handlePayment = async (data) => {
        const res = await loadRazorpayScript();
        console.log('loadRazorpayScript',res);
        
    
        if (!res) {
          alert("Razorpay SDK failed to load");
          return;
        }
    
        // Call your backend to create order
        // const data = await fetch("/api/create-order", {
        //   method: "POST",
        // }).then((t) => t.json());
    
        const options = {
          key: data.key, // from Razorpay dashboard
          amount: data.amount,
          currency: "INR",
          name: "Siance Software",
          description: "Test Transaction",
          order_id: data.order_id,
    
          handler: function (response) {
            console.log("Payment Success:", response);
             verifyPayment(response)
            // send this response to backend for verification
            // response.razorpay_payment_id
            // response.razorpay_order_id
            // response.razorpay_signature
          },
     
          prefill: {
            name: "John Doe",
            email: "john@example.com",
            contact: "9999999999",
          },
    
          theme: {
            color: "#3399cc",
          },
        };
    
       
    
        const paymentObject = new window.Razorpay(options);
        paymentObject.open();
      };
    

  return (
    <>
      <style>{css}</style>
       <Navbar/> 
      <div className="sd-page">

        {/* BG grid */}
        <svg className="sd-grid-bg" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="sdgrid" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke={TAN} strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#sdgrid)"/>
        </svg>

        {/* ── HERO ── */}
        <div className="sd-hero" style={{ position: "relative", zIndex: 1 }}>
          <motion.img
            src={cat.img}
            alt="Office Interior"
            className="sd-hero-img"
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          />
          <div className="sd-hero-overlay" />

          <div className="sd-hero-content" style={{ maxWidth: "unset" }}>
            <div style={{ maxWidth: 1100, margin: "0 auto" }}>
              <motion.div {...fade(0.2)}>
                <div className="sd-hero-badge">
                  <span>{cat.icon}</span> {cat.label}
                </div>
                <div className="sd-gold-line" />
                <h1 className="sd-hero-title">{SERVICE.package_name}</h1>
                <div className="sd-hero-meta">
                  <span className="sd-hero-meta-item">
                    <Ico d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" size={13} color={GOLD}/>
                    <span style={{ color: GOLD, fontWeight: 600 }}>4.9</span>
                    <span style={{ opacity: 0.5 }}>· {REVIEWS.length} Reviews</span>
                  </span>
                  <span className="sd-hero-meta-item">
                    <Ico d="M12 2a7 7 0 0 1 7 7c0 5.25-7 13-7 13S5 14.25 5 9a7 7 0 0 1 7-7z" size={12} color={TAN}/>
                    Pan India
                  </span>
                  <span className="sd-hero-meta-item">
                    <Ico d={["M8 2v4","M16 2v4","M3 10h18","M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z"]} size={12} color={TAN}/>
                    {SERVICE.duration_days} Day Project
                  </span>
                  <span style={{ marginLeft: "auto" }}>
                    <span style={{
                      display: "flex", alignItems: "center", gap: 5,
                      background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.3)",
                      borderRadius: 8, padding: "4px 12px",
                      fontSize: 9, color: "#4ade80", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600,
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", display: "inline-block", boxShadow: "0 0 6px rgba(74,222,128,0.8)" }} />
                      Active
                    </span>
                  </span>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* ── BODY ── */}
        <div className="sd-body" style={{ position: "relative", zIndex: 1 }}>

          {/* ── LEFT COLUMN ── */}
          <div>

            {/* Stats strip */}
            <motion.div {...fade(0.1)} className="sd-stats">
              {[
                { val: `₹${(+SERVICE.base_price).toLocaleString("en-IN")}`, label: "Base Price / sq.ft" },
                { val: `${SERVICE.min_area}`, label: "Min Area (sq.ft)" },
                { val: `${SERVICE.max_area}`, label: "Max Area (sq.ft)" },
                { val: `${SERVICE.duration_days}d`, label: "Delivery" },
              ].map((s, i) => (
                <motion.div key={i} className="sd-stat-item"
                  whileHover={{ background: "rgba(231,134,16,0.06)" }}>
                  <div className="sd-stat-val">{s.val}</div>
                  <div className="sd-stat-label">{s.label}</div>
                </motion.div>
              ))}
            </motion.div>

            {/* About */}
            <motion.div {...fade(0.18)} className="sd-card" style={{ marginBottom: 24 }}>
              <div className="sd-card-head">
                <div className="sd-card-head-icon">📋</div>
                <div>
                  <div className="sd-card-title">About This Package</div>
                </div>
              </div>
              <div className="sd-card-body">
                <p className="sd-desc">{SERVICE.description}</p>
                <p className="sd-desc" style={{ marginTop: 14 }}>
                  Every workspace we design is a reflection of your brand's culture and ambition. We blend ergonomic principles with sophisticated aesthetics — creating offices where professionals feel proud to work and clients are consistently impressed. Our end-to-end approach means you never need to coordinate multiple vendors.
                </p>
                <div className="sd-gallery">
                  {[cat.img2, cat.img3].map((src, i) => (
                    <motion.img key={i} src={src} alt={`gallery ${i+1}`} className="sd-gallery-img"
                      whileHover={{ scale: 1.04, filter: "brightness(1)" }}
                      transition={{ duration: 0.4 }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Materials */}
            <motion.div {...fade(0.25)} className="sd-card" style={{ marginBottom: 24 }}>
              <div className="sd-card-head">
                <div className="sd-card-head-icon">🪵</div>
                <div><div className="sd-card-title">Materials Used</div></div>
              </div>
              <div className="sd-card-body">
                <div className="sd-chips" style={{ marginBottom: 20 }}>
                  {materials.map((m, i) => (
                    <motion.span key={i} className="sd-chip"
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3 + i * 0.07 }}
                      whileHover={{ scale: 1.05 }}>
                      {m}
                    </motion.span>
                  ))}
                </div>
                <div style={{ borderTop: "1px solid rgba(187,165,139,0.1)", paddingTop: 18 }}>
                  <div style={{ fontSize: 9, color: GOLD, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600, marginBottom: 12 }}>Work Scope</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {scope.map((s, i) => (
                      <span key={i} style={{
                        padding: "5px 13px", borderRadius: 20, fontSize: 9.5, fontWeight: 600,
                        letterSpacing: "0.08em", textTransform: "uppercase",
                        background: "rgba(231,134,16,0.1)", border: "1px solid rgba(231,134,16,0.25)",
                        color: GOLD,
                      }}>{s}</span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Process */}
            <motion.div {...fade(0.32)} className="sd-card" style={{ marginBottom: 32 }}>
              <div className="sd-card-head">
                <div className="sd-card-head-icon">🔧</div>
                <div><div className="sd-card-title">Our Process</div></div>
              </div>
              <div className="sd-card-body">
                {PROCESS.map((p, i) => (
                  <motion.div key={i} className="sd-process-item"
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.38 + i * 0.09, duration: 0.5, ease: [0.22,1,0.36,1] }}
                    whileHover={{ paddingLeft: 6 }}
                    style={{ transition: "padding 0.25s" }}>
                    <div className="sd-process-num">{p.step}</div>
                    <div>
                      <div className="sd-process-step-title">{p.title}</div>
                      <div className="sd-process-desc">{p.desc}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Reviews */}
            <motion.div {...fade(0.4)}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600, color: WHITE }}>Client Reviews</div>
                <div style={{ flex: 1, height: 1, background: "rgba(187,165,139,0.12)" }} />
              </div>

              {/* Rating summary */}
              <div className="sd-rating-summary">
                <div className="sd-rating-big">4.9</div>
                <div className="sd-rating-right">
                  <Stars rating={5} size={15} />
                  <div className="sd-rating-count">{REVIEWS.length} verified reviews</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  {[5,4,3].map(r => (
                    <div key={r} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 9, color: "rgba(187,165,139,0.4)", width: 8 }}>{r}</span>
                      <div style={{ width: 80, height: 4, borderRadius: 4, background: "rgba(187,165,139,0.1)", overflow: "hidden" }}>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: r === 5 ? "90%" : r === 4 ? "8%" : "2%" }}
                          transition={{ delay: 0.6, duration: 0.8, ease: "easeOut" }}
                          style={{ height: "100%", background: `linear-gradient(90deg, ${GOLD}, #f5c56a)`, borderRadius: 4 }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {REVIEWS.map((r, i) => (
                <motion.div key={r.id} className="sd-review-card"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45 + i * 0.1, duration: 0.5, ease: [0.22,1,0.36,1] }}
                  whileHover={{ translateY: -2 }}>
                  <div className="sd-review-header">
                    <div className="sd-review-avatar">{r.avatar}</div>
                    <div>
                      <div className="sd-review-name">{r.name}</div>
                      <div className="sd-review-role">{r.role}</div>
                      <div style={{ marginTop: 4 }}><Stars rating={r.rating} size={11} /></div>
                    </div>
                    <div className="sd-review-date">{r.date}</div>
                  </div>
                  <p className="sd-review-text">"{r.text}"</p>
                </motion.div>
              ))}
            </motion.div>

          </div>

          {/* ── SIDEBAR ── */}
          <div className="sd-sidebar">
            <motion.div {...fade(0.3)} className="sd-price-card">
              <div className="sd-price-shimmer" />
              <div className="sd-price-head">
                <div className="sd-price-label">Starting From</div>
                <div className="sd-price-amount">
                  ₹{(+SERVICE.base_price).toLocaleString("en-IN")}
                </div>
                <div className="sd-price-type">Per Square Foot</div>
              </div>

              <div className="sd-price-rows">
                {[
                  { label: "Duration", val: `${SERVICE.duration_days} days` },
                  { label: "Min Area", val: `${SERVICE.min_area} sq.ft` },
                  { label: "Max Area", val: `${SERVICE.max_area} sq.ft` },
                  { label: "Package ID", val: `#${String(SERVICE.id).padStart(4,"0")}` },
                  { label: "Status", val: <span style={{ color: "#4ade80", fontWeight: 700 }}>● Active</span> },
                ].map((row, i) => (
                  <div key={i} className="sd-price-row">
                    <span className="sd-price-row-label">{row.label}</span>
                    <span className="sd-price-row-val">{row.val}</span>
                  </div>
                ))}
              </div>

              <motion.button className="sd-book-btn"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.97 }}
                style={{ width: "calc(100% - 48px)", margin: "0 24px 12px" }}
                onClick={() => setShowModal(true)}>
                Book Now →
              </motion.button>

              <button className="sd-consult-btn"
                style={{ width: "calc(100% - 48px)", margin: "0 24px 24px" }}>
                Request a Consultation
              </button>
            </motion.div>

            {/* Why us card */}
            <motion.div {...fade(0.4)} className="sd-card">
              <div className="sd-card-head">
                <div className="sd-card-head-icon">✦</div>
                <div><div className="sd-card-title">Why Choose Us</div></div>
              </div>
              <div className="sd-card-body">
                {[
                  { icon: "🏆", text: "12+ Years of Premium Experience" },
                  { icon: "📐", text: "Custom Design for Every Client" },
                  { icon: "⏱", text: "On-Time Delivery Guaranteed" },
                  { icon: "🔒", text: "Post-Project Warranty Support" },
                  { icon: "💎", text: "Premium Grade Materials Only" },
                ].map((item, i) => (
                  <motion.div key={i}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.07 }}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "9px 0",
                      borderBottom: i < 4 ? "1px solid rgba(187,165,139,0.07)" : "none",
                    }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
                    <span style={{ fontSize: 11, color: "rgba(187,165,139,0.7)", fontWeight: 300, letterSpacing: "0.03em", lineHeight: 1.5 }}>{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* ── BOOKING MODAL ── */}
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              style={{
                position: "fixed", inset: 0, zIndex: 100,
                background: "rgba(10,7,3,0.88)", backdropFilter: "blur(10px)",
                display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
              }}>
              <motion.div
                initial={{ scale: 0.88, y: 28 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 16 }}
                transition={{ duration: 0.45, ease: [0.22,1,0.36,1] }}
                onClick={e => e.stopPropagation()}
                style={{
                  background: "linear-gradient(145deg, rgba(30,20,8,0.98), rgba(20,14,5,0.98))",
                  border: "1px solid rgba(231,134,16,0.3)",
                  borderRadius: 24, padding: "36px",
                  maxWidth: 420, width: "100%",
                  boxShadow: "0 40px 80px rgba(0,0,0,0.7)",
                  position: "relative",
                }}>
                <div style={{
                  position: "absolute", top: 0, left: "10%", width: "80%", height: 2,
                  background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
                }} />

                <div style={{ textAlign: "center", marginBottom: 24 }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>{cat.icon}</div>
                  <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 600, color: WHITE, marginBottom: 6 }}>
                    Confirm Booking
                  </h2>
                  <p style={{ fontSize: 10, color: TAN, letterSpacing: "0.16em", textTransform: "uppercase" }}>
                    {SERVICE.package_name}
                  </p>
                </div>

                {[
                  { label: "Category", value: cat.label },
                  { label: "Price", value: `₹${(+SERVICE.base_price).toLocaleString("en-IN")} / sq.ft` },
                  { label: "Duration", value: `${SERVICE.duration_days} days` },
                  { label: "Area Range", value: `${SERVICE.min_area} – ${SERVICE.max_area} sq.ft` },
                ].map((r, i) => (
                  <div key={r.label} className="sd-modal-row">
                    <span style={{ fontSize: 10, color: "rgba(187,165,139,0.45)", letterSpacing: "0.12em", textTransform: "uppercase" }}>{r.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: WHITE }}>{r.value}</span>
                  </div>
                ))}

                <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
                  <button onClick={() => setShowModal(false)} style={{
                    flex: 1, padding: "12px", borderRadius: 12,
                    background: "rgba(187,165,139,0.08)", border: "1px solid rgba(187,165,139,0.2)",
                    color: "rgba(255,255,255,0.6)", fontFamily: "'Josefin Sans', sans-serif",
                    fontSize: 10, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer",
                  }}>Cancel</button>
                  <motion.button onClick={()=>handleOrder()}
                  whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} style={{
                    flex: 2, padding: "12px", borderRadius: 12,
                    background: `linear-gradient(135deg, #f0a84a, ${GOLD}, #c97d22)`,
                    border: "none", color: DARK, fontFamily: "'Josefin Sans', sans-serif",
                    fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", cursor: "pointer",
                    boxShadow: `0 6px 20px rgba(231,134,16,0.35)`,
                  }}>
                    Confirm Booking →
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
      <Footer/>
    </>
  );
}
